import type { Promotion } from '@/lib/types';
import { formatMoney } from '@/lib/money';

function formatDateLabel(value?: string) {
  if (!value) return '';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const RELATIVE_TIME_FORMATTER = new Intl.RelativeTimeFormat(undefined, {
  numeric: 'always',
});

function resolveRelativeUnit(diffMs: number) {
  const absMs = Math.abs(diffMs);
  const units: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
    { unit: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
    { unit: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
    { unit: 'week', ms: 7 * 24 * 60 * 60 * 1000 },
    { unit: 'day', ms: 24 * 60 * 60 * 1000 },
    { unit: 'hour', ms: 60 * 60 * 1000 },
    { unit: 'minute', ms: 60 * 1000 },
  ];

  const selected = units.find((entry) => absMs >= entry.ms) || units[units.length - 1];
  const rawValue = diffMs / selected.ms;
  const roundedValue = rawValue >= 0 ? Math.ceil(rawValue) : Math.floor(rawValue);

  return {
    unit: selected.unit,
    value: roundedValue || (diffMs >= 0 ? 1 : -1),
  };
}

function formatRelativePointLabel(
  value: string | undefined,
  mode: 'start' | 'end',
  referenceDate = new Date(),
) {
  if (!value) return '';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  const diffMs = parsed.getTime() - referenceDate.getTime();
  if (Math.abs(diffMs) < 60 * 1000) {
    return mode === 'start' ? 'Starts now' : 'Ends now';
  }

  const relative = resolveRelativeUnit(diffMs);
  const relativeText = RELATIVE_TIME_FORMATTER.format(relative.value, relative.unit);

  if (mode === 'start') {
    return relative.value < 0 ? `Started ${relativeText.replace(/ ago$/, ' ago')}` : `Starts ${relativeText}`;
  }

  return relative.value < 0 ? `Ended ${relativeText.replace(/ ago$/, ' ago')}` : `Ends ${relativeText}`;
}

function formatPromotionDateRangeLabel(
  promotion: Pick<Promotion, 'start_date' | 'end_date'>,
) { 
  const startLabel = formatDateLabel(promotion.start_date);
  const endLabel = formatDateLabel(promotion.end_date);

  if (startLabel && endLabel) {
    return `${startLabel} - ${endLabel}`;
  }

  if (startLabel) return `Starts ${startLabel}`;
  if (endLabel) return `Ends ${endLabel}`;
  return '';
}

export function formatPromotionDiscountLabel(
  promotion: Pick<Promotion, 'discount_type' | 'discount_value' | 'custom_label' | 'description' | 'title'>,
  /**
   * A currency CODE ("GHS", "NGN", "XOF"), not a symbol. Passing the code lets
   * `formatMoney` pick the right symbol, the right fraction digits, and the
   * thousands grouping for that currency.
   *
   * This parameter used to be a symbol that defaulted to `'$'`, and both live
   * guest-menu call sites passed nothing — so a 15 cedi discount on a Ghanaian
   * menu rendered "$15 off". It also went through a bare template literal, so
   * 4.5 rendered "$4.5 off" rather than "₵4.50".
   */
  currency?: string | null,
) {
  if (promotion.custom_label?.trim()) {
    return promotion.custom_label.trim();
  }

  if (promotion.discount_type === 'percentage') {
    return `${promotion.discount_value}% off`;
  }

  if (promotion.discount_type === 'fixed') {
    return `${formatMoney(promotion.discount_value, currency)} off`;
  }

  if (promotion.discount_type === 'bogo') {
    return 'Buy 1 Get 1';
  }

  return promotion.description?.trim() || promotion.title?.trim() || 'Special offer';
}

export function formatPromotionTargetLabel(
  promotion: Pick<Promotion, 'target_type'>,
) {
  if (promotion.target_type === 'item') return 'Applies to selected items';
  if (promotion.target_type === 'category') return 'Applies to selected categories';
  if (promotion.target_type === 'collection') return 'Applies to this collection';
  if (promotion.target_type === 'menu') return 'Applies across this menu';
  if (promotion.target_type === 'business') return 'Applies across this business';
  return '';
}

export function formatPromotionScheduleLabel(
  promotion: Pick<Promotion, 'start_date' | 'end_date' | 'schedule_rules'>,
) {
  const now = new Date();
  const startsAt = promotion.start_date ? new Date(promotion.start_date) : undefined;
  const endsAt = promotion.end_date ? new Date(promotion.end_date) : undefined;

  if (startsAt && !Number.isNaN(startsAt.getTime()) && now.getTime() < startsAt.getTime()) {
    return formatRelativePointLabel(promotion.start_date, 'start', now);
  }

  if (endsAt && !Number.isNaN(endsAt.getTime())) {
    return formatRelativePointLabel(promotion.end_date, 'end', now);
  }

  if (startsAt && !Number.isNaN(startsAt.getTime())) {
    return formatRelativePointLabel(promotion.start_date, 'start', now);
  }

  const scheduleType =
    promotion.schedule_rules && typeof promotion.schedule_rules === 'object'
      ? String((promotion.schedule_rules as Record<string, unknown>).type || '').trim()
      : '';

  if (scheduleType === 'specific_dates') return 'Scheduled for selected dates';
  if (scheduleType === 'date_range') return 'Scheduled for a date range';
  if (scheduleType === 'annual') return 'Repeats on an annual schedule';
  if (scheduleType) return 'Scheduled offer';

  return '';
}

export function decoratePromotionForTemplate(
  promotion: Promotion,
  options?: { currencySymbol?: string },
): Promotion {
  const discountLabel = formatPromotionDiscountLabel(promotion, options?.currencySymbol);
  const targetLabel = formatPromotionTargetLabel(promotion);
  const scheduleLabel = formatPromotionScheduleLabel(promotion);
  const startsInLabel = formatRelativePointLabel(promotion.start_date, 'start');
  const endsInLabel = formatRelativePointLabel(promotion.end_date, 'end');
  const timingLabel = (() => {
    const now = new Date();
    const startsAt = promotion.start_date ? new Date(promotion.start_date) : undefined;
    const endsAt = promotion.end_date ? new Date(promotion.end_date) : undefined;

    if (startsAt && !Number.isNaN(startsAt.getTime()) && now.getTime() < startsAt.getTime()) {
      return startsInLabel;
    }

    if (endsAt && !Number.isNaN(endsAt.getTime())) {
      return endsInLabel;
    }

    return startsInLabel || '';
  })();
  const dateRangeLabel = formatPromotionDateRangeLabel(promotion);

  return {
    ...promotion,
    title: promotion.title?.trim() || promotion.custom_label?.trim() || discountLabel || 'Special offer',
    badge_label: promotion.custom_label?.trim() || discountLabel,
    discount_label: discountLabel,
    target_label: targetLabel,
    timing_label: timingLabel,
    starts_in_label: startsInLabel,
    ends_in_label: endsInLabel,
    date_range_label: dateRangeLabel,
    schedule_label: timingLabel || scheduleLabel,
    claim_label: 'Claim Offer',
  };
}
